import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import CustomSelect from "../CustomSelect";
import CustomLabel from "../CustomLabel";
import ConfirmDialog from "../ConfirmDialog";
import { COLORS } from "../../assets/theme";
import {
  bookSession,
  cancelAttendance,
  getClassById,
  getSessionDetail,
  listRedeemablePackagesForSession,
  readMaterializedSessionId,
} from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { ClassSession, ClientPackage, RosterEntry } from "./types";
import {
  ENROLLMENT_MODE_OPTIONS,
  formatMoney,
  formatStatusLabel,
  formatTime,
  isAttendanceBooked,
  isSessionCancelled,
  isSessionStartInPast,
  labelForValue,
  rosterRowKey,
} from "./classUtils";
import { useClientOptions } from "./useClassOptions";
import CancelClassSessionDialog from "./CancelClassSessionDialog";
import RedeemablePackagePickerDialog from "./RedeemablePackagePickerDialog";
import ClassBookingModePromptDialog from "./ClassBookingModePromptDialog";
import NoActivePackageConfirmDialog from "./NoActivePackageConfirmDialog";
import ClassSeriesSessionsDialog from "./ClassSeriesSessionsDialog";
import QuickEditClassCapacityDialog from "./QuickEditClassCapacityDialog";
import ClassEnrollDialog from "./ClassEnrollDialog";

interface SessionRosterDialogProps {
  open: boolean;
  businessId?: string;
  sessionId: string | null;
  onClose: () => void;
  /** Fired after a booking/removal so the caller can refresh its session list. */
  onRosterChanged?: () => void;
  /** Adds a "Cancel session" action — used when this is the calendar's class modal. */
  allowSessionCancel?: boolean;
}

/**
 * The "press a class tile" panel: one call returns the session, its class
 * definition and the full roster. Staff can add a client (drop-in booking) or
 * remove one — removal credits back a package session or cancels the invoice.
 *
 * The session it shows may be projected rather than real: a date the class's
 * recurrence rule produces, with no appointment row behind it and a
 * `v:<class>:<date>` id. Reads work either way. The first write materializes
 * the row, and its id changes at that moment — so the id is tracked in state
 * and replaced from the write's response instead of being read off the prop
 * again.
 */
const SessionRosterDialog: React.FC<SessionRosterDialogProps> = ({
  open,
  businessId,
  sessionId,
  onClose,
  onRosterChanged,
  allowSessionCancel = false,
}) => {
  const { options: clientOptions, onSearchInput: onClientSearchInput } =
    useClientOptions(businessId, open);
  const [session, setSession] = React.useState<ClassSession | null>(null);
  // The id this panel is currently addressing. Starts as the prop, but is
  // replaced by the real uuid the moment a write materializes the session.
  const [activeSessionId, setActiveSessionId] = React.useState<string | null>(
    sessionId
  );
  const [loading, setLoading] = React.useState(false);
  const [booking, setBooking] = React.useState(false);
  const [clientId, setClientId] = React.useState("");
  const [removeTarget, setRemoveTarget] = React.useState<RosterEntry | null>(null);
  const [cancelSessionOpen, setCancelSessionOpen] = React.useState(false);
  const [packagePickerOpen, setPackagePickerOpen] = React.useState(false);
  const [redeemablePackages, setRedeemablePackages] = React.useState<ClientPackage[]>(
    []
  );
  const [pendingClientId, setPendingClientId] = React.useState("");
  const [packageBookingSubmitting, setPackageBookingSubmitting] = React.useState(
    false
  );
  const [noPackageConfirmOpen, setNoPackageConfirmOpen] = React.useState(false);
  const [noPackageSubmitting, setNoPackageSubmitting] = React.useState(false);
  const [capacityDialogOpen, setCapacityDialogOpen] = React.useState(false);
  const [bookingModePromptOpen, setBookingModePromptOpen] = React.useState(false);
  const [enrollDialogOpen, setEnrollDialogOpen] = React.useState(false);
  const [seriesSessionsOpen, setSeriesSessionsOpen] = React.useState(false);
  // null = unknown; otherwise how many upcoming sessions (including this one)
  // the class has *materialized*. On a recurring class this undercounts on
  // purpose — most dates are still projected — which is why the recurrence
  // rule below is checked first.
  const [upcomingSessionCount, setUpcomingSessionCount] = React.useState<
    number | null
  >(null);
  const [hasRecurrenceRule, setHasRecurrenceRule] = React.useState(false);

  const fetchSession = React.useCallback(
    async (overrideId?: string) => {
      const targetId = overrideId || activeSessionId;
      if (!targetId) return;
      setLoading(true);
      try {
        const detail = await getSessionDetail(targetId);
        setSession(detail);

        const classId = detail?.class?.id;
        if (classId) {
          try {
            const classDetail = await getClassById(String(classId));
            setUpcomingSessionCount(classDetail?.upcoming_sessions?.length ?? null);
            setHasRecurrenceRule(Boolean(classDetail?.recurrence_frequency));
          } catch {
            // Unknown — default to still asking (for BOTH-mode classes) rather
            // than silently hiding the series-enroll option; worst case the
            // client only had this one session anyway and "Just This Session"
            // is still correct.
            setUpcomingSessionCount(null);
            setHasRecurrenceRule(false);
          }
        } else {
          setUpcomingSessionCount(null);
          setHasRecurrenceRule(false);
        }
      } catch (error) {
        toast.error(getErrorMessage(error, "Failed to load the session."));
        setSession(null);
      } finally {
        setLoading(false);
      }
    },
    [activeSessionId]
  );

  React.useEffect(() => {
    if (!open || !sessionId) return;
    setClientId("");
    setActiveSessionId(sessionId);
  }, [open, sessionId]);

  React.useEffect(() => {
    if (!open || !activeSessionId) return;
    void fetchSession();
  }, [open, activeSessionId, fetchSession]);

  const roster = React.useMemo(() => session?.roster || [], [session]);
  // Cancelled attendance rows stay in the roster for history but don't count
  // toward capacity — this is what keeps the count in sync with the
  // calendar tile's booked_count, which only counts BOOKED rows.
  const bookedRoster = React.useMemo(
    () => roster.filter((entry) => isAttendanceBooked(entry.status)),
    [roster]
  );
  const capacity = session?.class?.capacity;
  const cancelled = isSessionCancelled(session?.status);
  // A projected session has no row behind it yet — its roster is who *will* be
  // on it (enrollments with sessions left on their block), not who is.
  const isProjectedSession = session?.is_virtual === true;
  // The backend doesn't reject booking a session whose start time has
  // already passed, so this is enforced here — you can't add someone to a
  // class that's already begun.
  const isPastSession = isSessionStartInPast(session?.start_at);
  const isFull =
    typeof capacity === "number" &&
    capacity > 0 &&
    bookedRoster.length >= capacity;

  // A client can be re-booked after cancelling, so only an active (BOOKED)
  // spot should block them from being picked again.
  const bookableClients = React.useMemo(() => {
    const taken = new Set(bookedRoster.map((entry) => String(entry.client_id)));
    return clientOptions.filter((option) => !taken.has(option.value));
  }, [clientOptions, bookedRoster]);

  // `forceDropIn` is only ever true when staff explicitly picked "Don't use a
  // package" from an open picker — an omitted clientPackageId on its own
  // still means "auto-pick" to the backend, not "skip packages".
  const bookClient = async (
    targetClientId: string,
    clientPackageId: string | undefined,
    forceDropIn: boolean,
    setSubmitting: (value: boolean) => void
  ) => {
    if (!activeSessionId) return;
    setSubmitting(true);
    try {
      const response = await bookSession(
        activeSessionId,
        targetClientId,
        clientPackageId,
        forceDropIn
      );
      // Booking a projected session writes the row — from here on the session
      // answers to a uuid, and the `v:` id it was addressed with is gone.
      const materialized = readMaterializedSessionId(response);
      if (materialized && materialized !== activeSessionId) {
        setActiveSessionId(materialized);
      }

      toast.success(
        clientPackageId
          ? "Client booked — a session was redeemed from the selected package."
          : forceDropIn
          ? "Client booked and charged the drop-in price."
          : "Client booked. If they have a matching package a session was redeemed, otherwise they were invoiced."
      );
      setClientId("");
      setPackagePickerOpen(false);
      setNoPackageConfirmOpen(false);
      setPendingClientId("");
      setRedeemablePackages([]);
      await fetchSession(materialized || undefined);
      onRosterChanged?.();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to book the client."));
    } finally {
      setSubmitting(false);
    }
  };

  // Never books straight from the button — always surfaces a modal first:
  // the package picker when the client has at least one redeemable package
  // (even just one, so staff explicitly confirms it rather than it being
  // silently auto-applied), or a confirmation that there's no active package
  // and this will be invoiced instead.
  const bookSingleSession = async () => {
    if (!activeSessionId || !clientId || isPastSession) return;
    setBooking(true);
    let packages: ClientPackage[] = [];
    try {
      packages = await listRedeemablePackagesForSession(activeSessionId, clientId);
    } catch (error) {
      setBooking(false);
      toast.error(getErrorMessage(error, "Failed to check available packages."));
      return;
    }
    setBooking(false);

    setPendingClientId(clientId);
    if (packages.length >= 1) {
      setRedeemablePackages(packages);
      setPackagePickerOpen(true);
    } else {
      setNoPackageConfirmOpen(true);
    }
  };

  const enrollmentMode = session?.class?.enrollment_mode;
  // DROP_IN-only classes only ever support the single-session /book path,
  // FIXED_ROSTER-only classes only ever support the /enroll path — either one
  // sent to the wrong endpoint is rejected server-side. BOTH is the only mode
  // where there's an actual choice to ask about.
  const isFixedRosterOnly = enrollmentMode === "FIXED_ROSTER";

  // A recurring class always has more dates ahead, whether or not they've been
  // materialized yet — the rule is the source of truth, not the row count.
  // Only fall back to counting materialized sessions when there's no rule.
  const hasFutureDates =
    hasRecurrenceRule ||
    upcomingSessionCount === null ||
    upcomingSessionCount > (isPastSession ? 0 : 1);

  // A BOTH-mode class supports drop-in and block enrollment at once, so adding
  // a client from here is ambiguous — ask which they want instead of silently
  // picking one. Only worth asking if there's actually more than this one
  // session to enroll into.
  const hasSeriesToEnrollInto = enrollmentMode === "BOTH" && hasFutureDates;

  // Enrolling only ever touches this class's future sessions server-side, so
  // it stays valid even from a past session's panel — as long as there are
  // still dates ahead. Single-session booking never is once this session's
  // start time has passed, so the button falls back to "Enroll" where that's
  // possible, or is disabled outright where it isn't (DROP_IN, which the
  // /enroll endpoint itself rejects, or a one-off class with nothing left).
  const canEnrollAsFallback = enrollmentMode !== "DROP_IN" && hasFutureDates;
  const bookingAction: "BOOK" | "ENROLL" | "BLOCKED" = isPastSession
    ? canEnrollAsFallback
      ? "ENROLL"
      : "BLOCKED"
    : isFixedRosterOnly
    ? "ENROLL"
    : "BOOK";

  const handleBook = () => {
    if (!activeSessionId || !clientId || bookingAction === "BLOCKED") return;
    if (bookingAction === "ENROLL") {
      setEnrollDialogOpen(true);
      return;
    }
    if (hasSeriesToEnrollInto) {
      setBookingModePromptOpen(true);
      return;
    }
    void bookSingleSession();
  };

  const handleRemove = async () => {
    if (!activeSessionId || !removeTarget?.client_id) return;
    try {
      await cancelAttendance(activeSessionId, String(removeTarget.client_id));
      toast.success("Client removed from the session and refunded.");
      await fetchSession();
      onRosterChanged?.();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to remove the client."));
    } finally {
      setRemoveTarget(null);
    }
  };

  return (
    <>
      <CustomDialog
        open={open}
        onClose={onClose}
        paperSx={{ minWidth: { sm: 720 }, maxWidth: 900 }}
        title={
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
              {session?.class?.name || "Class Session"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#666" }}>
              {session
                ? `${formatTableDate(session.start_at)} · ${formatTime(
                    session.start_at
                  )} - ${formatTime(session.end_at)} · ${
                    session.unit?.name || "-"
                  }`
                : ""}
            </Typography>
          </Box>
        }
        actions={
          <>
            {allowSessionCancel && session && !cancelled && (
              <CustomButton
                label="Cancel Session"
                variant="outlined"
                startIcon={<EventBusyIcon />}
                textColor="#d32f2f"
                borderColor="#d32f2f"
                onClick={() => setCancelSessionOpen(true)}
                sx={{ mr: "auto" }}
              />
            )}
            {(hasRecurrenceRule ||
              (typeof upcomingSessionCount === "number" &&
                upcomingSessionCount > 1)) && (
              <CustomButton
                label="Other Sessions"
                variant="outlined"
                onClick={() => setSeriesSessionsOpen(true)}
              />
            )}
            <CustomButton label="Close" variant="outlined" onClick={onClose} />
          </>
        }
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !session ? (
          <Typography sx={{ color: "#777", fontSize: 14 }}>
            Session not found.
          </Typography>
        ) : (
          <>
            {isProjectedSession && (
              <Box
                sx={{
                  border: "1px dashed #b794f4",
                  bgcolor: "#faf5ff",
                  borderRadius: 2,
                  p: 1.5,
                  mb: 2,
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#553c9a" }}>
                  This date is projected from the class schedule — no session
                  record exists yet. The names below are the enrollments that
                  will fill it. Booking anyone in creates the real session.
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                mb: 2,
                alignItems: "center",
              }}
            >
              <Chip
                size="small"
                label={`${bookedRoster.length}${
                  capacity ? ` / ${capacity}` : ""
                } booked`}
                sx={{ fontWeight: 600 }}
              />
              {!cancelled && (
                <Tooltip title="Edit class capacity">
                  <IconButton
                    size="small"
                    onClick={() => setCapacityDialogOpen(true)}
                  >
                    <EditIcon sx={{ color: COLORS.MainColor, width: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              {session.class?.coach?.full_name && (
                <Chip size="small" label={`Coach: ${session.class.coach.full_name}`} />
              )}
              {session.class?.category?.name && (
                <Chip size="small" label={session.class.category.name} />
              )}
              {session.class?.enrollment_mode && (
                <Chip
                  size="small"
                  label={`Enrollment: ${labelForValue(
                    ENROLLMENT_MODE_OPTIONS.map((o) => ({
                      label: o.label,
                      value: o.value,
                    })),
                    session.class.enrollment_mode
                  )}`}
                />
              )}
              <Chip
                size="small"
                label={`Drop-in ${formatMoney(session.class?.price)}`}
              />
              {session.status && (
                <Chip
                  size="small"
                  label={formatStatusLabel(session.status)}
                  color={cancelled ? "error" : "default"}
                />
              )}
            </Box>

            {!cancelled && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    mb:
                      bookingAction === "ENROLL" || bookingAction === "BLOCKED"
                        ? 0.5
                        : 2,
                  }}
                >
                  <Box sx={{ flex: "1 1 260px" }}>
                    <CustomLabel text="Add client" fontSize={14} fontWeight={500} />
                    <CustomSelect
                      value={clientId}
                      options={bookableClients}
                      onSearchInput={onClientSearchInput}
                      onChange={(v: any) => setClientId(String(v ?? ""))}
                      placeholder="Select a client"
                      searchable
                      disabled={
                        bookingAction === "BLOCKED" ||
                        (bookingAction === "BOOK" && isFull)
                      }
                      fullWidth
                    />
                  </Box>
                  <CustomButton
                    label={
                      bookingAction === "BLOCKED"
                        ? "Session Already Passed"
                        : bookingAction === "ENROLL"
                        ? "Enroll"
                        : isFull
                        ? "Session Full"
                        : "Book"
                    }
                    variant="contained"
                    bgColor={COLORS.MainColor}
                    textColor={COLORS.WhiteText}
                    loading={booking}
                    disabled={
                      !clientId ||
                      bookingAction === "BLOCKED" ||
                      (bookingAction === "BOOK" && isFull) ||
                      booking
                    }
                    onClick={handleBook}
                    sx={{ mb: 0.5 }}
                  />
                </Box>
                {bookingAction === "BLOCKED" && (
                  <Typography sx={{ fontSize: 12, color: "#d32f2f", mb: 2 }}>
                    This session's start time has already passed and the class
                    has no dates left to enroll into.
                  </Typography>
                )}
                {isPastSession && bookingAction === "ENROLL" && (
                  <Typography sx={{ fontSize: 12, color: "#777", mb: 2 }}>
                    This session already happened — adding a client here buys
                    them a block of the class's upcoming sessions instead.
                  </Typography>
                )}
                {!isPastSession && isFixedRosterOnly && (
                  <Typography sx={{ fontSize: 12, color: "#777", mb: 2 }}>
                    This class is fixed-membership only — adding a client here
                    buys them a block of upcoming sessions, not just this one.
                  </Typography>
                )}
                {bookingAction === "BOOK" && isFull && (
                  <Typography sx={{ fontSize: 12, color: "#d32f2f", mb: 2 }}>
                    Session full —{" "}
                    <Box
                      component="span"
                      onClick={() => setCapacityDialogOpen(true)}
                      sx={{
                        color: COLORS.MainColor,
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      increase capacity
                    </Box>
                    .
                  </Typography>
                )}
              </>
            )}

            <CustomLabel
              text="Membership Clients"
              fontSize={15}
              fontWeight={700}
              sx={{ mb: 1, display: "block" }}
            />
            <Box sx={{ overflowX: "auto" }}>
              {roster.length === 0 ? (
                <Typography sx={{ color: "#777", fontSize: 14 }}>
                  Nobody is booked into this session yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Paid with</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roster.map((entry, index) => {
                      // A projected entry has no attendance record — `id` is
                      // null — so there is nothing to cancel yet.
                      const projected = entry.is_projected === true;
                      const canRemove =
                        !cancelled &&
                        !projected &&
                        isAttendanceBooked(entry.status);
                      return (
                        <TableRow
                          key={rosterRowKey(entry, index)}
                          sx={{
                            opacity: isAttendanceBooked(entry.status) ? 1 : 0.6,
                            ...(projected
                              ? { bgcolor: "#faf5ff", fontStyle: "italic" }
                              : {}),
                          }}
                        >
                          <TableCell>
                            {entry.client?.full_name || "-"}
                            {projected && (
                              <Chip
                                size="small"
                                label="Projected"
                                variant="outlined"
                                sx={{
                                  ml: 1,
                                  height: 20,
                                  fontSize: 11,
                                  borderStyle: "dashed",
                                  color: "#6b46c1",
                                  borderColor: "#b794f4",
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>{formatStatusLabel(entry.status)}</TableCell>
                          <TableCell>{formatStatusLabel(entry.source)}</TableCell>
                          <TableCell>
                            {projected
                              ? "From their block"
                              : entry.client_package_id
                              ? "Package"
                              : "Invoice"}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={
                                projected
                                  ? "Nothing to cancel yet — this spot is only reserved by their enrollment"
                                  : canRemove
                                  ? "Remove from session"
                                  : "Already cancelled"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!canRemove}
                                  onClick={() => setRemoveTarget(entry)}
                                >
                                  <PersonRemoveIcon
                                    sx={{
                                      color: canRemove ? "#d32f2f" : "#D3D3D3",
                                      width: 20,
                                    }}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>
          </>
        )}
      </CustomDialog>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        title="Remove client from session"
        message={`Remove ${
          removeTarget?.client?.full_name || "this client"
        } from the session? If they paid with a package the session is credited back, otherwise their invoice is cancelled.`}
        confirmButtonText="Remove"
        onConfirm={handleRemove}
      />

      <CancelClassSessionDialog
        open={cancelSessionOpen}
        session={session}
        onClose={() => setCancelSessionOpen(false)}
        onCancelled={async () => {
          // Cancelling a projected session materializes it as CANCELLED, so
          // the id this panel holds is stale — reopen from the caller's list.
          await fetchSession();
          onRosterChanged?.();
        }}
      />

      <RedeemablePackagePickerDialog
        open={packagePickerOpen}
        clientName={
          clientOptions.find((option) => option.value === pendingClientId)?.label
        }
        dropInPrice={session?.class?.price}
        packages={redeemablePackages}
        submitting={packageBookingSubmitting}
        onClose={() => {
          if (packageBookingSubmitting) return;
          setPackagePickerOpen(false);
          setPendingClientId("");
          setRedeemablePackages([]);
        }}
        onChoose={(clientPackageId) =>
          void bookClient(
            pendingClientId,
            clientPackageId,
            clientPackageId === undefined,
            setPackageBookingSubmitting
          )
        }
      />

      <ClassBookingModePromptDialog
        open={bookingModePromptOpen}
        className={session?.class?.name}
        submitting={false}
        onClose={() => setBookingModePromptOpen(false)}
        onBookOnce={() => {
          setBookingModePromptOpen(false);
          void bookSingleSession();
        }}
        onEnrollSeries={() => {
          setBookingModePromptOpen(false);
          setEnrollDialogOpen(true);
        }}
      />

      <NoActivePackageConfirmDialog
        open={noPackageConfirmOpen}
        clientName={
          clientOptions.find((option) => option.value === pendingClientId)?.label
        }
        dropInPrice={session?.class?.price}
        submitting={noPackageSubmitting}
        onClose={() => {
          if (noPackageSubmitting) return;
          setNoPackageConfirmOpen(false);
          setPendingClientId("");
        }}
        onConfirm={() =>
          void bookClient(pendingClientId, undefined, false, setNoPackageSubmitting)
        }
      />

      {/* Block enrollment is priced per session now, so it runs through the
          same quote-then-confirm dialog the Classes page uses rather than a
          one-shot call from here. */}
      <ClassEnrollDialog
        open={enrollDialogOpen}
        businessId={businessId}
        classItem={session?.class || null}
        clientId={clientId || undefined}
        clientName={
          clientOptions.find((option) => option.value === clientId)?.label
        }
        onClose={() => {
          setEnrollDialogOpen(false);
          // Cleared on close, not on success: clearing it while the dialog is
          // still open would unlock its client picker and reset the result.
          setClientId("");
        }}
        onEnrolled={async () => {
          await fetchSession();
          onRosterChanged?.();
        }}
      />

      <ClassSeriesSessionsDialog
        open={seriesSessionsOpen}
        classId={session?.class?.id ? String(session.class.id) : null}
        className={session?.class?.name}
        capacity={session?.class?.capacity}
        currentSessionId={activeSessionId}
        onClose={() => setSeriesSessionsOpen(false)}
      />

      <QuickEditClassCapacityDialog
        open={capacityDialogOpen}
        classId={session?.class?.id ? String(session.class.id) : null}
        className={session?.class?.name}
        currentCapacity={session?.class?.capacity}
        onClose={() => setCapacityDialogOpen(false)}
        onUpdated={async () => {
          setCapacityDialogOpen(false);
          await fetchSession();
          onRosterChanged?.();
        }}
      />
    </>
  );
};

export default SessionRosterDialog;
